import * as yup from 'yup';

import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';
import { kaskoSBSchema } from '@/features/Sparten/shared/schema/kaskoSchema/kaskoSchema';
import { vorschaedenSchema } from '@/features/Sparten/shared/schema/kfz/vorschaedenSchema/vorschaedenSchema';
import { vorversicherungSchema } from '@/features/Sparten/shared/schema/kfz/vorversicherungSchema/vorversicherungSchema';
import { KASKOTYP } from '@/features/Sparten/shared/types';
import { getNextDay } from '@/utils/dates/dates';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import { useInputStore } from '@/features/Sparten';
import { abweichenderHalterSchema } from '@/features/Sparten/shared/schema/kfz/abweichenderHalterSchema/abweichenderHalterSchema';

export const isNotHaftpflichtCallback = (val: string): boolean =>
  val !== KASKOTYP.HAFTPFLICHT;

const versicherungsbeginnInputValidator = (value: Date | null): boolean => {
  if (value === null) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (useInputStore().fahrzeug.erstbesitzer === true) {
    const erstZulassung =
      useInputStore().fahrzeug.erstzulassung !== undefined
        ? (useInputStore().fahrzeug.erstzulassung as Date)
        : today;

    return value >= erstZulassung;
  }

  const erstmaligeZulassungAufVN =
    useInputStore().fahrzeug.erstmaligeZulassungAufVN !== undefined
      ? (useInputStore().fahrzeug.erstmaligeZulassungAufVN as Date)
      : today;

  return value >= erstmaligeZulassungAufVN;
};

const saisonkennzeichenSchema = yup.object({
  monatsAnfang: yup.string().default(''),
  monatsEnde: yup.string().default(''),
});

export const baseInputSchema = yup.object({
  abweichenderHalter: abweichenderHalterSchema,
  deckungssumme: yup.string().default(''),
  demvRabattiert: yup.boolean().default(true),
  gapDeckung: yup
    .boolean()
    .default(false)
    .when(['kaskoTyp', 'fahrzeug.finanzierungsart'], {
      is: (val: string, finanzierungsart: string) =>
        val !== KASKOTYP.HAFTPFLICHT &&
        getSimpleChoiceMeta(
          SIMPLE_CHOICE.FINANZIERUNGSART,
          SIMPLE_CHOICE_META.FINANZIERUNGSART_NOT_SELF_FINANCING,
        ).includes(finanzierungsart),
      then: yup.boolean().required(),
    }),
  isSaisonkennzeichen: yup.boolean().default(false),
  kaskoSB: kaskoSBSchema,
  kaskoTyp: yup.string().required().default(''),
  kollisionMitTieren: yup.boolean().default(false).when('kaskoTyp', {
    is: isNotHaftpflichtCallback,
    then: yup.boolean().required(),
  }),
  saisonkennzeichen: saisonkennzeichenSchema,
  schutzbriefleistungen: yup.boolean().default(false),
  tierbissfolgeschaeden: yup.string().default('').when('kaskoTyp', {
    is: isNotHaftpflichtCallback,
    then: yup.string().required(),
  }),
  versicherungsbeginn: yup
    .date()
    .nullable()
    .default(getNextDay())
    .required()
    .test(
      'isValidVersicherungsbeginn',
      'Der Versicherungsbeginn darf nicht vor der Zulassung sein.',
      versicherungsbeginnInputValidator,
    ),
  vorschaeden: vorschaedenSchema,
  vorversicherung: vorversicherungSchema,
  werkstattbindung: yup.string().default('').when('kaskoTyp', {
    is: isNotHaftpflichtCallback,
    then: yup.string().required(),
  }),
  zahlart: yup.string().default(''),
  zahlweise: yup.string().default(''),
});
